// Shell-wide compose state: whether the sheet is open and what it was opened with (a blank
// message, an existing draft, or a reply, reply all or forward the server started). `c`, the
// palette, Reply / Reply all / Forward and the unsubscribe ladder's mailto rung all open it here.
import { useSyncExternalStore } from "react";
import type { Address, ComposeStartRequest } from "@shared/api";

export interface ComposeOpenRequest {
  /** The account to send from (default: the first connected account). */
  accountId?: string | undefined;
  /** An existing draft to continue (the Drafts list, the mailto rung). */
  draftId?: string | undefined;
  /** A cardless reply: the message joins this thread; the server computes the threading. */
  replyToThreadKey?: string | undefined;
  /** The thread's subject, shown as the placeholder ("Re: …" is derived by the server). */
  threadSubject?: string | null | undefined;
  to?: Address[] | undefined;
  cc?: Address[] | undefined;
  subject?: string | undefined;
  /** Rich text to start with (a restored, never-saved message). */
  bodyHtml?: string | undefined;
  includeSignature?: boolean | undefined;
  /** How the server started this draft; the sheet's title says so. */
  mode?: ComposeStartRequest["mode"] | undefined;
  /**
   * The revision the server started the draft at: closed with nothing changed, the draft is
   * dropped quietly (nothing was typed, so there is nothing to keep).
   */
  startedRev?: number | undefined;
  /** Calm words to show once (e.g. a forward's files that could not be copied). */
  notice?: string | null | undefined;
}

export interface ComposeState {
  open: boolean;
  request: ComposeOpenRequest;
  /** Bumps on every open so the sheet starts fresh even when opened twice with the same input. */
  seq: number;
}

let state: ComposeState = { open: false, request: {}, seq: 0 };
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

export const composeStore = {
  open(request: ComposeOpenRequest = {}) {
    state = { open: true, request, seq: state.seq + 1 };
    emit();
  },
  close() {
    if (!state.open) return;
    state = { ...state, open: false };
    emit();
  },
  get(): ComposeState {
    return state;
  },
};

export function useComposeState(): ComposeState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
    () => state,
  );
}
